import { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Spinner } from "@/components/ui/spinner";
import ACL_RELATIONSHIPS from "acl-relationships";
import { BaseForm } from "components/BaseForm";
import useAuthorized from "hooks/useAuthorized";
import AccountDetailsCard from "routes/swoogo/AccountDetailsCard";
import {
  credentialsOf,
  isSaveBlocked,
  makeSchema,
} from "routes/swoogo/connectionForm";
import { swoogoKeys } from "routes/swoogo/queryKeys";
import VisibleFoldersCard from "routes/swoogo/VisibleFoldersCard";
import SwoogoService from "services/Swoogo";

// Pre-fills the name field when no account is stored, so it is never empty.
const DEFAULT_ACCOUNT_NAME = "Swoogo Account";

const savedSelectionOf = folders =>
  (folders ?? []).filter(folder => folder.is_visible).map(folder => folder.id);

const ConnectionPage = () => {
  const queryClient = useQueryClient();

  const canManage = useAuthorized([ACL_RELATIONSHIPS.swoogo.manageConnection]);
  const canReadFolders = useAuthorized([ACL_RELATIONSHIPS.swoogo.readFolders]);

  const { data: connection, isSuccess: isLoaded } = useQuery({
    queryKey: swoogoKeys.connection(),
    queryFn: SwoogoService.getConnection,
    enabled: canManage,
  });

  const { data: storedFolders, isLoading: isLoadingFolders } = useQuery({
    queryKey: swoogoKeys.folders(),
    queryFn: SwoogoService.getFolders,
    enabled: Boolean(connection?.is_configured) && canReadFolders,
  });

  const testMutation = useMutation({
    mutationFn: SwoogoService.testConnection,
  });
  const testedCredentials = testMutation.data?.ok
    ? testMutation.variables
    : undefined;
  const folders = testMutation.data?.ok
    ? testMutation.data.folders
    : (storedFolders?.folders ?? []);

  // The mutation, not the form, owns the failure: BaseForm replaces the API
  // message of a 5xx with a generic one, and the 502 "unreachable" message
  // must reach the user.
  const saveMutation = useMutation({
    mutationFn: SwoogoService.updateConnection,
    onSuccess: async response => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: swoogoKeys.connection() }),
        queryClient.invalidateQueries({ queryKey: swoogoKeys.status() }),
        queryClient.invalidateQueries({ queryKey: swoogoKeys.folders() }),
      ]);
      testMutation.reset();
      toast.success(
        response?.sync_started
          ? "Swoogo account saved. Sync started."
          : "Swoogo account saved. No sync was started."
      );
    },
  });
  const savedCredentials = saveMutation.isSuccess
    ? saveMutation.variables
    : undefined;

  const isStored = connection?.source === "database";
  const acceptedCredentials = useMemo(
    () => [testedCredentials, savedCredentials].filter(Boolean),
    [testedCredentials, savedCredentials]
  );
  const schema = useMemo(() => makeSchema(isStored), [isStored]);
  const shouldDisableSubmit = values =>
    isSaveBlocked({ isStored, acceptedCredentials, values });

  const handleSubmit = values => {
    const knownFolderIds = new Set(folders.map(folder => folder.id));
    // A blank credential retains the stored one, so it is not sent.
    saveMutation.mutate({
      account_name: values.account_name,
      ...credentialsOf(values),
      folder_ids: values.folder_ids.filter(id => knownFolderIds.has(id)),
    });
  };

  const saveErrorMessage = saveMutation.isError
    ? (saveMutation.error?.response?.data?.message ??
      "The credentials could not be saved.")
    : undefined;

  if (!canManage) {
    return <Navigate to="/settings" replace />;
  }

  // The form's default values are cached by react-hook-form on its first
  // render, so it mounts only once the stored selection is known.
  if (!isLoaded || isLoadingFolders) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-muted-foreground">
        <Spinner />
      </div>
    );
  }

  const defaultValues = {
    account_name: connection.account_name ?? DEFAULT_ACCOUNT_NAME,
    api_key: "",
    api_secret: "",
    folder_ids: savedSelectionOf(storedFolders?.folders),
  };

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-start tw:font-sans tw:text-foreground">
      <div className="tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
        <BaseForm
          enablePageTitle
          pageTitle="Swoogo Account"
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          shouldDisableSubmit={shouldDisableSubmit}
          errorMessage={saveErrorMessage}
          submitButtonText="Save and Sync"
        >
          <AccountDetailsCard
            connection={connection}
            testMutation={testMutation}
          />
          {canReadFolders && (
            <VisibleFoldersCard
              isConfigured={connection.is_configured}
              isLoading={isLoadingFolders}
              folders={folders}
              onRefreshed={testMutation.reset}
            />
          )}
        </BaseForm>
      </div>
    </div>
  );
};

export default ConnectionPage;
